import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { 
  Clock, 
  BookOpen, 
  AlertTriangle, 
  Monitor, 
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react'

const ExamInstructions = ({ exam, hasAttempted, attemptLoading, onStartExam, onCancel }) => {
  console.log('ExamInstructions - hasAttempted:', hasAttempted);
  console.log('ExamInstructions - attemptLoading:', attemptLoading);
  console.log('ExamInstructions - exam:', exam);
  const handleStartExam = () => {
    // Request fullscreen
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen()
    } else if (document.documentElement.webkitRequestFullscreen) {
      document.documentElement.webkitRequestFullscreen()
    } else if (document.documentElement.msRequestFullscreen) {
      document.documentElement.msRequestFullscreen()
    }
    
    onStartExam()
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <Card className="shadow-lg">
          <CardHeader className="bg-indigo-600 text-white">
            <CardTitle className="text-2xl font-bold flex items-center gap-2">
              <BookOpen className="w-6 h-6" />
              Exam Instructions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Exam Details */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Exam Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">Subject:</span>
                  <span>{exam.subject?.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">Class:</span>
                  <span>{exam.class?.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">Duration:</span>
                  <span>{exam.duration} minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">Total Questions:</span>
                  <span>{exam.questionPaper?.questions?.length || exam.questions?.length || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">Total Marks:</span>
                  <span>{exam.totalMarks}</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">Passing Marks:</span>
                  <span>{exam.passingMarks}</span>
                </div>
              </div>
            </div>

            {/* Important Instructions */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">Important Instructions</h3>
                  <ul className="space-y-2 text-yellow-700">
                    <li className="flex items-start gap-2">
                      <Monitor className="w-4 h-4 mt-1" />
                      <span>The exam will switch to full-screen mode</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Clock className="w-4 h-4 mt-1" />
                      <span>The timer will start immediately when you begin</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 mt-1" />
                      <span>Once submitted, you cannot retake the exam</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Exam Rules */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Info className="w-5 h-5 text-gray-600" />
                Exam Rules
              </h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm">Read each question carefully and select your answer</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm">Do not refresh the page or close the browser</span>
                </div>
              </div>
            </div>

            {/* Question Types */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Question Types</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-blue-100 text-blue-800">Multiple Choice</Badge>
                  <span className="text-sm">Select the correct answer from options A, B, C, D</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4 border-t">
              <Button
                variant="outline"
                onClick={onCancel}
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                onClick={handleStartExam}
                disabled={hasAttempted || attemptLoading}
                className={`px-8 py-2 ${
                  hasAttempted || attemptLoading
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {attemptLoading ? 'Checking...' : hasAttempted ? 'Exam Already Taken' : 'Start Exam'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ExamInstructions
